import { useEffect, useRef, useState } from 'react';
import type { Score, Team } from '../lib/types';

/**
 * Punktestand.
 *
 * Die Zahl zählt auf einen neuen Wert hoch oder herunter, statt zu springen. Das ist nicht nur
 * hübsch: Im Stream sieht man dadurch, dass sich etwas verändert hat, auch wenn man gerade
 * woanders hingeschaut hat.
 */
export function Punktestand({
  teams,
  scores,
  ausrichtung = 'horizontal',
}: {
  teams: Team[];
  scores: Score[];
  ausrichtung?: 'horizontal' | 'vertikal';
}) {
  return (
    <div className={`scores ${ausrichtung}`}>
      {teams.map((team) => {
        const punkte = scores.find((s) => s.teamId === team.id)?.total ?? 0;
        return (
          <div key={team.id} className="score-entry">
            <span className="score-name" style={{ background: team.color }}>
              {team.name}
            </span>
            <span className="score-value" style={{ color: team.color }}>
              <Zählwerk ziel={punkte} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Zählt in kleinen Schritten zum neuen Wert. */
function Zählwerk({ ziel }: { ziel: number }) {
  const [wert, setWert] = useState(ziel);
  const letzter = useRef(ziel);

  useEffect(() => {
    if (letzter.current === ziel) return;
    const von = letzter.current;
    const schritte = Math.min(Math.abs(ziel - von), 12);
    const richtung = Math.sign(ziel - von);
    let i = 0;

    const uhr = setInterval(() => {
      i += 1;
      const anteil = i / schritte;
      setWert(Math.round(von + (ziel - von) * anteil));
      if (i >= schritte) {
        clearInterval(uhr);
        setWert(ziel);
        letzter.current = ziel;
      }
    }, 45);

    // Falls zwischendurch ein neuer Wert kommt, beginnt die Zählung von vorn.
    return () => {
      clearInterval(uhr);
      letzter.current = ziel;
      setWert(ziel);
    };
  }, [ziel]);

  // Beim ersten Erscheinen wird nicht gezählt, sonst springt der Stand beim Laden.
  return <>{wert}</>;
}
